import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PasswordInput } from "../components/ui/PasswordInput";
import { toast } from "sonner";
import { Search, CheckCircle2 } from "lucide-react";

const initial = {
  email: "",
  password: "",
  business_name: "",
  giro: "",
  address: "",
  postal_code: "",
  colonia: "",
  city: "",
  state: "",
  whatsapp: "",
  promoter_name: "",
  promoter_id: "",
  access_pin: "",
};

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pid = params.get("pid");
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [promoterLocked, setPromoterLocked] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!pid) return;
    let cancelled = false;
    api
      .get(`/promoter/${encodeURIComponent(pid)}`)
      .then((r) => {
        if (cancelled) return;
        const promoterName = r.data?.name || "";
        if (promoterName) {
          setForm((f) => ({ ...f, promoter_name: promoterName, promoter_id: pid }));
          setPromoterLocked(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [pid]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    setBusy(true);
    try {
      await register(form);
      const hasPinAccess = !!form.access_pin.trim();
      toast.success(hasPinAccess ? "¡Cuenta creada con acceso gratuito!" : "¡Cuenta creada! Te llevamos al pago.");
      navigate(hasPinAccess ? "/dashboard" : "/dashboard?tab=pago");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrarte");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="overline text-[hsl(var(--primary))]">Únete al directorio</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">Registra tu comercio</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Captura tus datos. Después podrás pagar y activar tu anuncio.
        </p>

        <form onSubmit={submit} className="mt-6 bg-white border border-border rounded-2xl p-4 sm:p-6 space-y-5" data-testid="register-form">
          <Section title="Datos del negocio">
            <Field label="Nombre del negocio o giro principal *">
              <Input required value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Ej. Tacos El Sabor" data-testid="reg-business-name" />
            </Field>
            <Field label="Giro / categoría *">
              <Input required value={form.giro} onChange={(e) => set("giro", e.target.value)} placeholder="Ej. Taquería, Estética, Papelería" data-testid="reg-giro" />
            </Field>
            <Field label="Domicilio *">
              <Input required value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Calle y número" data-testid="reg-address" />
            </Field>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Código Postal *">
                <Input required value={form.postal_code} onChange={(e) => set("postal_code", e.target.value.replace(/\D/g, "").slice(0, 5))} inputMode="numeric" placeholder="06700" data-testid="reg-cp" />
              </Field>
              <Field label="Colonia">
                <Input value={form.colonia} onChange={(e) => set("colonia", e.target.value)} data-testid="reg-colonia" />
              </Field>
              <Field label="WhatsApp *">
                <Input required value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="5512345678" inputMode="numeric" data-testid="reg-whatsapp" />
                {form.whatsapp.length > 0 && form.whatsapp.length < 10 && (
                  <p className="text-[11px] text-amber-600 mt-1">{form.whatsapp.length}/10 dígitos — faltan {10 - form.whatsapp.length}</p>
                )}
                {form.whatsapp.length === 10 && (
                  <p className="text-[11px] text-green-600 mt-1">✓ Número completo</p>
                )}
              </Field>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Ciudad"><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="reg-city" /></Field>
              <Field label="Estado"><Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="reg-state" /></Field>
            </div>
          </Section>

          <Section title="Cuenta">
            <Field label="Contraseña *">
              <PasswordInput required value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Mín. 6 caracteres" data-testid="reg-password" />
              {form.password.length > 0 && form.password.length < 6 && (
                <p className="text-[11px] text-amber-600 mt-1">Faltan {6 - form.password.length} caracteres más</p>
              )}
              {form.password.length >= 6 && (
                <p className="text-[11px] text-green-600 mt-1">✓ Contraseña válida</p>
              )}
              {form.password.length === 0 && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  Iniciarás sesión con tu WhatsApp (capturado arriba) + esta contraseña.
                </div>
              )}
            </Field>
            <Field label="Correo electrónico (opcional)">
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="tu@correo.com — solo si quieres recibir avisos" data-testid="reg-email" />
              <div className="text-[11px] text-muted-foreground mt-1">
                No es obligatorio. Si lo capturas, lo usaremos para enviarte avisos importantes (renovaciones, ganadores de rifa, etc.).
              </div>
            </Field>
            <PromoterSearchField
              form={form}
              set={set}
              locked={promoterLocked}
            />
            <Field label="Código de acceso (opcional)">
              <Input
                value={form.access_pin}
                onChange={(e) => set("access_pin", e.target.value)}
                placeholder="Si tienes un código especial, captúralo aquí"
                data-testid="reg-access-pin"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Solo si tu asesor te proporcionó un código de acceso. Déjalo vacío si no tienes uno.
              </div>
            </Field>
          </Section>

          <div className="pt-2">
            <Button type="submit" disabled={busy} className="w-full btn-primary rounded-full py-6" data-testid="register-submit-btn">
              {busy ? "Creando cuenta…" : "Crear cuenta y continuar al pago"}
            </Button>
            <div className="text-sm text-center mt-3 text-muted-foreground">
              ¿Ya tienes cuenta? <Link to="/login" className="text-[hsl(var(--primary))] font-medium">Inicia sesión</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * PromoterSearchField: debounced autocomplete for promoter selection
 * ------------------------------------------------------------------------- */
function PromoterSearchField({ form, set, locked }) {
  const [query, setQuery] = useState(form.promoter_name || "");
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Sync external changes (e.g. from QR pid)
  useEffect(() => {
    setQuery(form.promoter_name || "");
  }, [form.promoter_name, form.promoter_id]);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const onType = (value) => {
    setQuery(value);
    // Clear promoter_id when user edits manually — they need to pick from results
    set("promoter_id", "");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/promoter-search?q=${encodeURIComponent(trimmed)}`);
        setResults(data || []);
        setShowDropdown(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const pick = (p) => {
    set("promoter_name", p.name);
    set("promoter_id", p.id);
    setQuery(p.name);
    setShowDropdown(false);
  };

  const selected = !!form.promoter_id;

  return (
    <Field label="Nombre de su promotor (opcional)">
      <div className="relative" ref={wrapperRef}>
        <div className="relative">
          <Input
            value={query}
            onChange={(e) => onType(e.target.value)}
            onFocus={() => results.length > 0 && setShowDropdown(true)}
            placeholder="Si un promotor te invitó, busca su nombre"
            readOnly={locked}
            data-testid="reg-promoter-name"
            className={locked ? "bg-muted/50" : ""}
          />
          {selected && !showDropdown && (
            <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600" data-testid="reg-promoter-confirmed" />
          )}
          {searching && !locked && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {showDropdown && !locked && results.length > 0 && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-56 overflow-auto" data-testid="reg-promoter-dropdown">
            {results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pick(p)}
                className="w-full text-left px-4 py-2.5 hover:bg-[#F5F3FF] transition-colors border-b border-border last:border-0 flex items-center gap-2"
                data-testid={`reg-promoter-option-${p.id}`}
              >
                <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium">{p.name}</span>
              </button>
            ))}
          </div>
        )}

        {showDropdown && !locked && !searching && results.length === 0 && query.trim().length >= 2 && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-border rounded-lg shadow-lg px-4 py-3" data-testid="reg-promoter-no-results">
            <span className="text-sm text-muted-foreground">
              No se encontraron promotores. Verifica el nombre o deja el campo vacío.
            </span>
          </div>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">
        {locked
          ? "Promotor asignado automáticamente por el código QR. Ya no es necesario capturarlo."
          : selected
            ? `Promotor seleccionado: ${form.promoter_name}. Si es correcto, continúa. Si no, borra y busca de nuevo.`
            : "Busca el nombre del promotor que te invitó y selecciónalo de la lista. Si no fuiste invitado, deja el campo vacío."}
      </div>
    </Field>
  );
}

const Section = ({ title, children }) => (
  <div className="space-y-4">
    <div className="overline text-muted-foreground border-b border-border pb-2">{title}</div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);